import React, { useRef, useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { getVariantProductInfoSelector } from '@modules/products/core/selectors';
import {
  getDefaultSubscriptionSelector,
  getPetSelector,
} from '@modules/subscriptions/core/selectors';
import { useClickOutside } from '@shared/index';

import { calcDeliveryDate } from '@modules/subscriptions/utils/calcDeliveryDate';
import { SubscriptionReactivationOrderItems } from './SubscriptionReactivationOrderItems';
import { SubscriptionReactivationRecipes } from './SubscriptionReactivationRecipes';
import { SubscriptionReactivationModal } from './reactivation-modal/SubscriptionReactivationModal';
import { Modal } from '@components/index';
import { KTSVG, toAbsoluteUrl, usePossessiveName } from '@helpers/index';

interface SubscriptionReactivationInfoProps {
  intro?: string;
  text?: string;
  textExtra?: string;
  buttonText: string;
  onReactivateClick: () => void;
}

export const SubscriptionReactivationInfo: React.FC<SubscriptionReactivationInfoProps> = ({
  intro,
  text,
  textExtra,
  buttonText,
  onReactivateClick,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const defaultSubscription = useSelector(getDefaultSubscriptionSelector);

  const pet = useSelector(getPetSelector);
  // Was `{pet?.name}'s Letzte Bestellübersicht` — an English apostrophe-s
  // possessive in German copy. The helper inflects per locale instead.
  const possessiveName = usePossessiveName(pet?.name);
  const productInfo = useSelector(getVariantProductInfoSelector);

  const totalSachetQuantity = productInfo?.sachets?.reduce(
    (total: any, sachet: any) => total + sachet.quantity,
    0,
  );
  const today = new Date().toISOString();
  const [dateFrom, dateTo] = calcDeliveryDate(today);

  useClickOutside(ref, () => {
    setIsModalOpen(false);
  });

  const handleClose = () => setIsModalOpen(false);

  const handleModalToggle = () => {
    setIsModalOpen(!isModalOpen);
  };

  return (
    <div className="subscription-reactivation-content">
      <h4 className="subscription-reactivation-order-headline px-[3rem] hidden md:block">
        <FormattedMessage
          id="SUBSCRIPTION.REACTIVATE.LAST_ORDER_TITLE"
          values={{ name: pet?.name ?? '', possessive: possessiveName }}
        />
      </h4>
      <div className="subscription-reactivation-text">
        <p>{intro}</p>
        <div className="subscription-reactivation-text-description">{text}</div>

        <span>{textExtra}</span>
      </div>

      <div className="subscription-reactivation-order">
        <h4 className="subscription-reactivation-order-headline md:hidden">
          <FormattedMessage
            id="SUBSCRIPTION.REACTIVATE.LAST_ORDER_TITLE"
            values={{ name: pet?.name ?? '', possessive: possessiveName }}
          />
        </h4>

        {defaultSubscription?.status === 'PAUSED' && (
          <div className="subscription-reactivation-action-wrapper">
            <div className="subscription-reactivation-action md:hidden">
              <button className="flex items-center font-bold gap-[8px]" onClick={handleModalToggle}>
                <KTSVG path={toAbsoluteUrl('/media/svg/general/edit.svg')} />
                <FormattedMessage id="SUBSCRIPTION.SHIPPING_AND_PAYMENT" />
              </button>
              {isModalOpen && (
                <Modal isOpen={true} handleClose={handleClose}>
                  <SubscriptionReactivationModal />
                </Modal>
              )}
            </div>

            <div className="subscription-reactivation-calendar md:hidden">
              <div className="subscription-reactivation-calendar-delivery">
                <p>Lieferdatum</p>
                <FormattedDate value={dateFrom} day="2-digit" /> -{' '}
                <FormattedDate value={dateTo} year="numeric" month="short" day="2-digit" />
              </div>
            </div>
          </div>
        )}

        <SubscriptionReactivationOrderItems totalSachetQuantity={totalSachetQuantity} />
        <SubscriptionReactivationRecipes />

        <button className="subscription-reactivation-button" onClick={onReactivateClick}>
          {buttonText}
        </button>
      </div>
    </div>
  );
};
